import type { ReactNode } from 'react';
import { formatAmount, formatCompact } from './format';

/* ==========================================================================
 *  Shared primitives
 *
 *  Small on purpose. Anything with real product logic belongs in the app that
 *  owns it; what lives here is the handful of things that must look identical
 *  across the merchant, employee and Quantum consoles.
 * ========================================================================== */

/* -------------------------------------------------------------------------- */
/*  StatusBadge                                                                */
/* -------------------------------------------------------------------------- */

type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/**
 * One canonical mapping from domain status to colour. Defined once so a
 * PENDING_REVIEW merchant is the same amber in the compliance queue as it is
 * in the Quantum merchant list.
 */
const TONE_BY_STATUS: Record<string, Tone> = {
  // Transactions
  SUCCESS: 'success',
  AUTHORIZED: 'success',
  PENDING: 'warning',
  INITIATED: 'neutral',
  PROCESSING: 'warning',
  QUEUED: 'neutral',
  FAILED: 'danger',
  CANCELLED: 'neutral',
  EXPIRED: 'neutral',
  REFUNDED: 'info',
  PARTIALLY_REFUNDED: 'info',
  DISPUTED: 'danger',
  REVERSED: 'danger',
  // KYC
  APPROVED: 'success',
  VERIFIED: 'success',
  DRAFT: 'neutral',
  PENDING_REVIEW: 'warning',
  ACTION_REQUIRED: 'warning',
  REUPLOAD_REQUESTED: 'warning',
  UNDER_REVIEW: 'info',
  REJECTED: 'danger',
  SUSPENDED: 'danger',
  // Settlements & payouts
  PAID: 'success',
  ON_HOLD: 'warning',
  // Risk
  NONE: 'neutral',
  LOW: 'success',
  MEDIUM: 'warning',
  HIGH: 'danger',
  CRITICAL: 'danger',
};

export function StatusBadge({ status, label }: { status: string; label?: string }) {
  const key = status?.toUpperCase() ?? '';
  const tone = TONE_BY_STATUS[key] ?? 'neutral';
  return <span className={`badge badge-${tone}`}>{label ?? key.replace(/_/g, ' ').toLowerCase()}</span>;
}

/* -------------------------------------------------------------------------- */
/*  Money                                                                      */
/* -------------------------------------------------------------------------- */

export function Money({
  amount,
  currency = 'INR',
  compact = false,
  className = '',
}: {
  /** Rupees as the API sends them: "1499.50". */
  amount: string | number | bigint | null | undefined;
  currency?: string;
  compact?: boolean;
  className?: string;
}) {
  const full = formatAmount(amount, currency);
  return (
    // The full value stays in the title so a compact KPI is never ambiguous.
    <span className={`numeral ${className}`} title={compact ? full : undefined}>
      {compact ? formatCompact(amount, currency) : full}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  KpiCard                                                                    */
/* -------------------------------------------------------------------------- */

export function KpiCard({
  label,
  value,
  delta,
  hint,
  icon,
}: {
  label: string;
  value: ReactNode;
  /** Percentage change against the comparison period. */
  delta?: number | null;
  hint?: string;
  icon?: ReactNode;
}) {
  return (
    <div className="panel px-4 py-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-2xs uppercase tracking-wider text-content-subtle">{label}</p>
        {icon && <span className="text-content-faint">{icon}</span>}
      </div>
      <p className="mt-2 text-xl font-semibold tracking-tight text-content">{value}</p>
      <div className="mt-1.5 flex items-center gap-2">
        {delta !== undefined && delta !== null && (
          <span className={`numeral text-2xs ${delta >= 0 ? 'text-brand-400' : 'text-danger'}`}>
            {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
          </span>
        )}
        {hint && <span className="text-2xs text-content-faint">{hint}</span>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  DataTable                                                                  */
/* -------------------------------------------------------------------------- */

export interface Column<T> {
  key: string;
  header: string;
  /** Right-align numeric columns; money reads correctly only that way. */
  align?: 'left' | 'right';
  width?: string;
  render: (row: T) => ReactNode;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  loading = false,
  empty,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  empty?: ReactNode;
}) {
  if (loading) {
    return (
      <div className="panel">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex gap-4 border-b border-line-subtle px-3 py-3.5 last:border-0">
            {columns.map((col) => (
              <div
                key={col.key}
                className="h-3 flex-1 animate-shimmer rounded bg-[linear-gradient(90deg,#111827_25%,#1E293B_50%,#111827_75%)] bg-[length:200%_100%]"
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) return <div className="panel">{empty ?? <EmptyState title="Nothing here yet" />}</div>;

  return (
    <div className="panel overflow-hidden">
      <div className="scrollbar-thin overflow-x-auto">
        <table className="table-riskpay">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} style={col.width ? { width: col.width } : undefined}
                    className={col.align === 'right' ? '!text-right' : undefined}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? 'cursor-pointer' : undefined}
              >
                {columns.map((col) => (
                  <td key={col.key} className={col.align === 'right' ? 'text-right' : undefined}>
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  EmptyState & Spinner                                                       */
/* -------------------------------------------------------------------------- */

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      <p className="text-sm font-medium text-content">{title}</p>
      {description && <p className="max-w-sm text-xs leading-relaxed text-content-subtle">{description}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function Spinner({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" className="opacity-20" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}
